import type { MenuProps } from 'antd';
import { Tooltip } from 'antd';
import {
  CalendarOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UsergroupAddOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';

import { canScheduleInspection } from '@/shared/status';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { InspectionTableData } from './useInspectionsTable';

interface ActionMenuItemsParams {
  record: InspectionTableData;
  itemIndex?: number;
  hasPermission: (permissions: UserPermissions[]) => boolean;
  isInspectorUser: () => boolean;
  onProviderAccept?: (assignmentId: string, inspectionId: string) => void;
  onProviderReject?: (assignmentId: string, inspectionId: string) => void;
  onAssignInspector?: (assignmentId: string, inspectionId: string) => void;
  onInspectorAccept?: (assignmentId: string, inspectionId: string) => void;
  onInspectorReject?: (assignmentId: string, inspectionId: string) => void;
  onCreateIssue?: (itemId: string) => void;
  onSubmitProviderReport?: (itemId: string, inspectionId: string) => void;
  onSchedule: (itemId: string, inspectionId: string) => void;
  onAssign: (itemId: string, inspectionId: string) => void;
}

export function generateActionMenuItems({
  record,
  itemIndex,
  hasPermission,
  isInspectorUser,
  onProviderAccept,
  onProviderReject,
  onAssignInspector,
  onInspectorAccept,
  onInspectorReject,
  onCreateIssue,
  onSubmitProviderReport,
  onSchedule,
  onAssign,
}: ActionMenuItemsParams): MenuProps['items'] {
  const menuItems: MenuProps['items'] = [];

  const item = itemIndex !== undefined
    ? record.items?.[itemIndex]
    : record.items?.[0];

  const itemId = item?.id || (itemIndex !== undefined ? `${record.id}-${itemIndex}` : record.id);
  const itemStatus = item?.status;

  const canScheduleItem = itemStatus !== undefined
    ? canScheduleInspection(itemStatus)
    : false;

  const canProviderResponse = itemStatus === InspectionItemStatus.AguardandoAceitePrestadora;
  const canAssignInspectorStatus = itemStatus === InspectionItemStatus.AguardandoDirecionamentoInspetor;
  const canInspectorResponseStatus = itemStatus === InspectionItemStatus.AguardandoAceiteInspetor;
  const canSubmitProviderReportStatus = itemStatus === InspectionItemStatus.AguardandoPreenchimentoPrestadora;

  const canProviderResponsePermission = hasPermission([
    UserPermissions.inspections_accept_provider,
    UserPermissions.inspections_reject_provider,
    UserPermissions.inspections_refuse_provider
  ]);

  const canSchedulePermission = hasPermission([
    UserPermissions.inspections_schedule_provider,
    UserPermissions.inspections_schedule_inspector
  ]);

  const canAssignPermission = hasPermission([
    UserPermissions.inspections_redirect_provider
  ]);

  const canRedirectInspectorPermission = hasPermission([UserPermissions.inspections_redirect_inspector]);

  const inspectorHasAccepted = itemStatus !== undefined &&
    itemStatus >= InspectionItemStatus.AguardandoAgendamento;

  const getAssignmentId = () => item?.assignments?.[0]?.id || record.assignmentId || record.id;

  // Provider Accept/Reject
  if (canProviderResponsePermission && canProviderResponse) {
    menuItems.push(
      {
        key: 'provider-accept',
        type: 'item',
        label: (
          <div className="flex items-center gap-2 px-2 py-1">
            <CheckCircleOutlined className="text-green-500" />
            <span className="font-medium">Aceitar Inspeção</span>
          </div>
        ),
        onClick: (e) => {
          e?.domEvent?.stopPropagation();
          onProviderAccept?.(getAssignmentId(), record.id);
        },
      },
      {
        key: 'provider-reject',
        type: 'item',
        label: (
          <div className="flex items-center gap-2 px-2 py-1">
            <CloseCircleOutlined className="text-red-500" />
            <span className="font-medium">Recusar Inspeção</span>
          </div>
        ),
        onClick: (e) => {
          e?.domEvent?.stopPropagation();
          onProviderReject?.(getAssignmentId(), record.id);
        },
      }
    );
  }

  // Assign Inspector
  if (canAssignInspectorStatus && onAssignInspector && canRedirectInspectorPermission) {
    menuItems.push({
      key: 'assign-inspector',
      type: 'item',
      label: (
        <div className="flex items-center gap-2 px-2 py-1">
          <UsergroupAddOutlined className="text-purple-500" />
          <span className="font-medium">Direcionar para Inspetor</span>
        </div>
      ),
      onClick: (e) => {
        e?.domEvent?.stopPropagation();
        onAssignInspector(getAssignmentId(), record.id);
      },
    });
  }

  // Inspector Accept/Reject
  if (canInspectorResponseStatus && isInspectorUser()) {
    menuItems.push(
      {
        key: 'inspector-accept',
        type: 'item',
        label: (
          <div className="flex items-center gap-2 px-2 py-1">
            <CheckCircleOutlined className="text-green-500" />
            <span className="font-medium">Aceitar Inspeção</span>
          </div>
        ),
        onClick: (e) => {
          e?.domEvent?.stopPropagation();
          onInspectorAccept?.(getAssignmentId(), record.id);
        },
      },
      {
        key: 'inspector-reject',
        type: 'item',
        label: (
          <div className="flex items-center gap-2 px-2 py-1">
            <CloseCircleOutlined className="text-red-500" />
            <span className="font-medium">Recusar Inspeção</span>
          </div>
        ),
        onClick: (e) => {
          e?.domEvent?.stopPropagation();
          onInspectorReject?.(getAssignmentId(), record.id);
        },
      }
    );
  }

  if (canSubmitProviderReportStatus && onSubmitProviderReport && canProviderResponsePermission) {
    menuItems.push({
      key: 'submit-provider-report',
      type: 'item',
      label: (
        <div className="flex items-center gap-2 px-2 py-1">
          <FileTextOutlined className="text-amber-500" />
          <span className="font-medium">Enviar Laudo para Banco</span>
        </div>
      ),
      onClick: (e) => {
        e?.domEvent?.stopPropagation();
        onSubmitProviderReport(itemId, record.id);
      },
    });
  }

  if (onCreateIssue && isInspectorUser() && inspectorHasAccepted) {
    menuItems.push({
      key: 'create-issue',
      type: 'item',
      label: (
        <div className="flex items-center gap-2 px-2 py-1">
          <ExclamationCircleOutlined className="text-orange-500" />
          <span className="font-medium">Cadastrar Pendência</span>
        </div>
      ),
      onClick: (e) => {
        e?.domEvent?.stopPropagation();
        onCreateIssue(itemId);
      },
    });
  }

  // Schedule
  if (canSchedulePermission) {
    menuItems.push({
      key: 'schedule',
      type: 'item',
      label: canScheduleItem ? (
        <div className="flex items-center gap-2 px-2 py-1">
          <CalendarOutlined className="text-blue-500" />
          <span className="font-medium">Agendar</span>
        </div>
      ) : (
        <Tooltip
          title={
            <div className="text-center">
              <div className="font-medium mb-1">Agendamento não disponível</div>
              <div className="text-xs text-gray-200">
                Só será possível agendar após o inspetor aceitar a inspeção
              </div>
            </div>
          }
          color="rgba(0, 0, 0, 0.85)"
          placement="top"
        >
          <div className="flex items-center gap-2 px-2 py-1 text-gray-400 cursor-not-allowed">
            <CalendarOutlined />
            <span>Agendar</span>
          </div>
        </Tooltip>
      ),
      disabled: !canScheduleItem,
      ...(canScheduleItem && {
        onClick: (e) => {
          e?.domEvent?.stopPropagation();
          onSchedule(itemId, record.id);
        }
      }),
    });
  }

  const canAssignProviderStatus = itemStatus === InspectionItemStatus.AguardandoAceitePrestadora || itemStatus === InspectionItemStatus.AguardandoDirecionamento;

  if (canAssignPermission) {
    menuItems.push({
      key: 'assign',
      type: 'item',
      label: canAssignProviderStatus ? (
        <div className="flex items-center gap-2 px-2 py-1">
          <SendOutlined className="text-green-500" />
          <span className="font-medium">Direcionar</span>
        </div>
      ) : (
        <Tooltip
          title={
            <div className="text-center">
              <div className="font-medium mb-1">Direcionamento não disponível</div>
              <div className="text-xs text-gray-200">
                A inspeção já foi direcionada para uma prestadora
              </div>
            </div>
          }
          color="rgba(0, 0, 0, 0.85)"
          placement="top"
        >
          <div className="flex items-center gap-2 px-2 py-1 text-gray-400 cursor-not-allowed">
            <SendOutlined />
            <span>Direcionar</span>
          </div>
        </Tooltip>
      ),
      disabled: !canAssignProviderStatus,
      ...(canAssignProviderStatus && {
        onClick: (e) => {
          e?.domEvent?.stopPropagation();
          onAssign(itemId, record.id);
        }
      }),
    });
  }

  return menuItems;
}
